/**
 * What the player played, file by file and span by span, reported to the
 * server for the audit log (threat model C08).
 *
 * @module
 */
import type { PlanFile } from "./plan";

export interface PlayedSpan {
  file_id: string;
  from_utc: string;
  to_utc: string;
}

/** A jump of more than this between ticks is a seek, which ends a span. */
const JUMP_MS = 2000;

export class PlayLog {
  private open = new Map<string, { from: number; to: number }>();
  private done: PlayedSpan[] = [];
  private last: number | null = null;

  /** Call each tick with the playhead, whether it is playing, and every source's files. */
  update(now: number, playing: boolean, files: readonly PlanFile[]): void {
    const jumped = this.last !== null && Math.abs(now - this.last) > JUMP_MS;
    this.last = now;
    if (!playing || jumped) {
      this.closeAll();
      if (!playing) return;
    }
    const here = new Set(files.filter((f) => f.fromUtcMs <= now && now < f.toUtcMs).map((f) => f.id));
    for (const [id] of this.open) if (!here.has(id)) this.close(id);
    for (const id of here) {
      const span = this.open.get(id);
      if (span) span.to = now;
      else this.open.set(id, { from: now, to: now });
    }
  }

  /** Spans finished, and those still open up to now; open ones carry on from here. */
  take(): PlayedSpan[] {
    for (const [id, span] of this.open) {
      if (span.to > span.from) this.done.push(spanOf(id, span));
      this.open.set(id, { from: span.to, to: span.to });
    }
    const out = this.done;
    this.done = [];
    return out;
  }

  private close(id: string): void {
    const span = this.open.get(id);
    this.open.delete(id);
    if (span && span.to > span.from) this.done.push(spanOf(id, span));
  }

  private closeAll(): void {
    for (const [id] of this.open) this.close(id);
    this.last = null;
  }
}

function spanOf(id: string, s: { from: number; to: number }): PlayedSpan {
  return { file_id: id, from_utc: new Date(s.from).toISOString(), to_utc: new Date(s.to).toISOString() };
}
